import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ArrowUpRight } from "lucide-react";
import Logo from "@/components/Logo";

const NAV = [
  { to: "/product", label: "Product" },
  { to: "/why-lexifide", label: "Why Lexifide" },
  { to: "/resources", label: "Resources" },
  { to: "/contact", label: "Contact" },
];

const Header = () => {
  const { pathname } = useLocation();
  const isHome = pathname === "/";
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Dark header on Home hero (top of page), light otherwise
  const isDark = isHome && !scrolled;

  const wrapBg = isDark
    ? "bg-transparent"
    : scrolled
    ? "bg-white/85 backdrop-blur-md border-b border-[#e4e4e7]"
    : "bg-white border-b border-[#e4e4e7]";

  const linkBase =
    "font-display font-medium text-[14px] tracking-tight transition-colors duration-200";
  const linkClr = isDark
    ? "text-white/75 hover:text-white"
    : "text-[#52525b] hover:text-[#090a0f]";
  const activeClr = isDark ? "text-white" : "text-[#090a0f]";

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${wrapBg}`}
    >
      <div className="max-w-[1380px] mx-auto px-6 lg:px-10 h-[68px] flex items-center justify-between">
        <Logo variant={isDark ? "dark" : "light"} testId="header-logo" />

        <nav className="hidden lg:flex items-center gap-9" data-testid="primary-nav">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `${linkBase} ${isActive ? activeClr : linkClr}`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <a
            href="/get-started?type=diagnostic"
            data-testid="header-cta-diagnostic"
            className={
              isDark
                ? "inline-flex items-center gap-2 px-4 py-2 text-[13px] font-display font-semibold text-[#090a0f] bg-white hover:bg-[#0047ff] hover:text-white border border-white transition-all duration-200"
                : "inline-flex items-center gap-2 px-4 py-2 text-[13px] font-display font-semibold text-white bg-[#090a0f] hover:bg-[#0047ff] border border-[#090a0f] hover:border-[#0047ff] transition-all duration-200"
            }
          >
            Book diagnostic
            <ArrowUpRight className="w-3.5 h-3.5" strokeWidth={2.5} />
          </a>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          aria-label="Toggle menu"
          onClick={() => setOpen((v) => !v)}
          className={`lg:hidden inline-flex items-center justify-center h-10 w-10 ${
            isDark ? "text-white" : "text-[#090a0f]"
          }`}
        >
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {/* Mobile menu */}
      {open && (
        <div
          className="lg:hidden bg-white border-b border-[#e4e4e7]"
          data-testid="mobile-menu"
        >
          <div className="px-6 py-6 flex flex-col gap-4">
            {NAV.map((n) => (
              <Link
                key={n.to}
                to={n.to}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="font-display text-[18px] font-medium text-[#090a0f]"
              >
                {n.label}
              </Link>
            ))}
            <a
              href="/get-started?type=diagnostic"
              onClick={() => setOpen(false)}
              data-testid="mobile-cta-diagnostic"
              className="mt-2 inline-flex items-center gap-2 px-4 py-3 text-[14px] font-display font-semibold text-white bg-[#090a0f] w-fit"
            >
              Book diagnostic <ArrowUpRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
